import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Navigation } from 'utils/Navigation';
import { MainMenu, MainMenuLeafItem, MainMenuGroupItem, MainMenuSubItem } from 'components/mainmenu/MainMenu';
import { Meta, EUserRole } from 'utils/meta';
import { MainStage } from 'MainStage';
import { AuthStage } from 'AuthStage';
import { SceneFactory } from 'scenes/SceneFactory';
import { GeneralSettingsSceneFactory } from 'scenes/settings/general/GeneralSettingsScene';
import { ExportSceneFactory } from 'scenes/settings/export/view/ExportScene';
import { ContactUsSceneFactory } from 'scenes/contactus/view/ContactUsScene';
import { TeamHolidaysSceneFactory } from 'scenes/statistics/teamholidays/view/TeamHolidaysScene';
import { MeetTheTeamSceneFactory } from 'scenes/meettheteam/view/MeetTheTeamScene';
import { DashboardSceneFactory } from 'scenes/dashboard/view/DashboardScene';
import { CalendarSceneFactory } from 'scenes/calendar/view/CalendarScene';
import { TeamSceneFactory } from 'scenes/settings/team/TeamScene';
import { ProfileSettingsSceneFactory } from 'scenes/app/profilesettings/ProfileSettingsScene';
import { LoginSceneFactory } from 'scenes/auth/login/LoginScene';
import { RegisterSceneFactory } from 'scenes/auth/register/RegisterScene';
import { AcceptInvitationSceneFactory } from 'scenes/auth/acceptinvitation/AcceptInvitationScene';
import { ForgotPasswordSceneFactory } from 'scenes/auth/forgotpassword/ForgotPasswordScene';
import { ResetPasswordSceneFactory } from 'scenes/auth/resetpassword/ResetPasswordScene';
import { GA } from 'utils/GA';

// Unauthenticated pages, served by the same SPA bundle but without the
// authenticated shell (no side menu, no Meta).
const AUTH_ROUTES: string[] = ["/login.rsc", "/register.rsc", "/acceptInvitation.rsc", "/forgotPassword.rsc", "/resetPassword.rsc"];

function isAuthRoute(): boolean {
    return AUTH_ROUTES.indexOf(Navigation.currentState.path) >= 0;
}

async function init() {
    Navigation.init();

    // Auth scenes never need Meta or the menu.
    initAuthScenes();

    if (!isAuthRoute()) {
        await Meta.load();
        initMenu();
        initScenes();
    }

    reactRender();

    GA.sendCurrentPageView();
}

function initMenu() {
    MainMenu.registerTopItem(new MainMenuLeafItem("Dashboard", "dashboard", "/index.rsc"));
    MainMenu.registerTopItem(new MainMenuLeafItem("Calendar", "vertical_split", "/calendar.rsc"));
    MainMenu.registerTopItem(new MainMenuLeafItem("Meet the Team", "group", "/team.rsc"));
    MainMenu.registerTopItem(new MainMenuGroupItem("Statistics", "bar_chart", [
        new MainMenuSubItem("Team Holidays", "/statistics/teamHolidays.rsc"),
    ]));

    if (Meta.get().userRole.level >= EUserRole.HR.level) {
        MainMenu.registerBottomItem(new MainMenuGroupItem("Settings", "settings", [
            new MainMenuSubItem("General", "/settings/general.rsc"),
            new MainMenuSubItem("Team", "/settings/team.rsc"),
            new MainMenuSubItem("Export", "/settings/export.rsc"),
        ]));
    }
    MainMenu.registerBottomItem(new MainMenuLeafItem("Profile Settings", "account_box", "/profileSettings.rsc"));
    MainMenu.registerBottomItem(new MainMenuLeafItem("Contact Us", "contact_support", "/contactus.rsc"));
    MainMenu.registerBottomItem(new MainMenuLeafItem("Logout", "exit_to_app", "/logout.jsp"));

}

function initScenes() {
    SceneFactory.registerScene("/index.rsc", new DashboardSceneFactory());
    SceneFactory.registerScene("/calendar.rsc", new CalendarSceneFactory());
    SceneFactory.registerScene("/team.rsc", new MeetTheTeamSceneFactory());
    SceneFactory.registerScene("/statistics/teamHolidays.rsc", new TeamHolidaysSceneFactory());
    SceneFactory.registerScene("/settings/general.rsc", new GeneralSettingsSceneFactory());
    SceneFactory.registerScene("/settings/team.rsc", new TeamSceneFactory());
    SceneFactory.registerScene("/settings/export.rsc", new ExportSceneFactory());
    SceneFactory.registerScene("/profileSettings.rsc", new ProfileSettingsSceneFactory());
    SceneFactory.registerScene("/contactus.rsc", new ContactUsSceneFactory());
}

function initAuthScenes() {
    SceneFactory.registerScene("/login.rsc", new LoginSceneFactory());
    SceneFactory.registerScene("/register.rsc", new RegisterSceneFactory());
    SceneFactory.registerScene("/acceptInvitation.rsc", new AcceptInvitationSceneFactory());
    SceneFactory.registerScene("/forgotPassword.rsc", new ForgotPasswordSceneFactory());
    SceneFactory.registerScene("/resetPassword.rsc", new ResetPasswordSceneFactory());
}

function reactRender() {
    ReactDOM.render(
        (isAuthRoute() ? <AuthStage /> : <MainStage />),
        document.getElementById("root"),
    )
}

window.addEventListener("load", () => {
    init();
});

Navigation.onStateChanged.addListener(() => {
    GA.sendCurrentPageView();
    reactRender();
});
